import React, { memo, useCallback, useEffect, useRef, useState, type ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuid } from 'uuid';

const REPLACED_MARKER = 'data-replaced';
const CLONE_MARKER = 'data-clone';

type LegacyDOMBridgeProps = {
	selectors: string[];

	attempts?: number;
	retryDelay?: number;
	rootNode?: HTMLElement;
	portalNode: JSX.Element;
	replacePlaceholderWith?: {
		elementName: string;
		attributes?: Record<string, string>;
	};
	deleteExistingContent?: boolean;
};

function getLegacyElements(
	selectors: string[],
	rootNode: HTMLElement | Document = window.document,
): Element[] {
	if (!rootNode) {
		return [];
	}

	return Array.from(rootNode.querySelectorAll(selectors.join(',')));
}

function getDomElementAttrsMap(domElement: Element): Record<string, string> {
	if (!domElement.hasAttributes()) {
		return {};
	}

	return Object.values(domElement.attributes).reduce<Record<string, string>>((acc, attr) => {
		acc[attr.name] = attr.value;
		return acc;
	}, {});
}

export const LegacyDOMBridge: React.NamedExoticComponent<LegacyDOMBridgeProps> = memo(
	function LegacyDOMBridge({
		selectors,
		attempts = 1,
		retryDelay = 50,
		rootNode,
		portalNode,
		replacePlaceholderWith,
		deleteExistingContent = true,
	}: LegacyDOMBridgeProps) {
		const [portals, setPortals] = useState<ReactPortal[]>([]);
		const workerInProgressRef = useRef(false);
		const portalsRef = useRef<ReactPortal[]>([]);

		const replaceDOMElementsWithPortal = useCallback(
			(attemptsLeft: number) => {
				workerInProgressRef.current = true;

				getLegacyElements(selectors, rootNode).forEach((node) => {
					const currentNodeAttrs = getDomElementAttrsMap(node);
					if (currentNodeAttrs[REPLACED_MARKER] || currentNodeAttrs[CLONE_MARKER]) {
						return; // do not clone clones or already cloned nodes
					}
					// @ts-expect-error FIXME: `true` here probably needs to be replaced with `"true"`
					node.setAttribute(REPLACED_MARKER, true);
					// @ts-expect-error FIXME: `true` here probably needs to be replaced with `"true"`
					currentNodeAttrs[CLONE_MARKER] = true;
					currentNodeAttrs['key'] = currentNodeAttrs.id || uuid();

					let targetNode = node;
					if (replacePlaceholderWith) {
						targetNode = window.document.createElement(replacePlaceholderWith.elementName);
						const attributes = replacePlaceholderWith.attributes || {};
						for (const attr in attributes) {
							targetNode.setAttribute(attr, attributes[attr]);
						}
						node.parentNode!.replaceChild(targetNode, node);
					}

					if (deleteExistingContent) {
						while (targetNode.firstChild) {
							targetNode.removeChild(targetNode.firstChild);
						}
					}
					if (portalNode) {
						const reactEl = React.cloneElement(portalNode, currentNodeAttrs);
						portalsRef.current.push(ReactDOM.createPortal(reactEl, targetNode));
					}
				});

				const remaining = attemptsLeft - 1;
				setPortals([...portalsRef.current]);

				if (remaining > 0) {
					window.setTimeout(() => {
						replaceDOMElementsWithPortal(remaining);
					}, retryDelay);
				} else {
					workerInProgressRef.current = false;
				}
			},
			[selectors, rootNode, portalNode, replacePlaceholderWith, deleteExistingContent, retryDelay],
		);

		useEffect(() => {
			if (attempts > 0 && !workerInProgressRef.current) {
				const timerId = setTimeout(() => {
					replaceDOMElementsWithPortal(attempts);
				}, 0);
				return () => clearTimeout(timerId);
			}
		}, [attempts, replaceDOMElementsWithPortal]);

		return <>{portals}</>;
	},
);
